import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { platformApi } from '../services/platformApi';
import { i18n } from '../utils/i18n';

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await platformApi.requestPasswordReset(email);
      setSuccess(true);
    } catch (err: any) {
      console.error('Password reset request failed:', err);
      let errorMessage = err.message || i18n.__('forgotPassword.requestFailed');
      
      // Handle specific error cases
      if (errorMessage.includes('User not found') || errorMessage.includes('404')) {
        errorMessage = i18n.__('forgotPassword.userNotFound');
      } else if (errorMessage.includes('Network error')) {
        errorMessage = i18n.__('forgotPassword.networkError');
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <CardTitle className="text-2xl">
              {i18n.__('vaiu.forgot_password.email_sent_title') || 'Check your email'}
            </CardTitle>
            <CardDescription className="mt-2">
              {i18n.__('vaiu.forgot_password.email_sent_description') || 
                `We've sent a password reset link to ${email}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertDescription>
                {i18n.__('vaiu.forgot_password.check_spam') || 
                  "If you don't see the email, check your spam folder."}
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2">
              <Button
                className="w-full"
                onClick={() => navigate('/')}
              >
                {i18n.__('vaiu.back_to_login') || 'Back to Login'}
              </Button>
              
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setSuccess(false);
                  setEmail('');
                }}
              >
                {i18n.__('vaiu.forgot_password.try_different_email') || 'Try a different email'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <Button
            variant="ghost"
            size="sm"
            className="w-fit -ml-2 mb-2"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {i18n.__('vaiu.back') || 'Back'}
          </Button>
          
          <div className="flex items-center space-x-2 mb-2">
            <Mail className="w-6 h-6 text-blue-600" />
            <CardTitle className="text-2xl">
              {i18n.__('vaiu.forgot_password.title') || 'Forgot your password?'}
            </CardTitle>
          </div>
          
          <CardDescription>
            {i18n.__('vaiu.forgot_password.description') || 
              "Enter your email address and we'll send you a link to reset your password."}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">
                {i18n.__('vaiu.email') || 'Email address'}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={i18n.__('forgotPassword.emailPlaceholder')}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className={error ? 'border-red-500' : ''}
                autoFocus
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading || !email.trim()}
            >
              {loading 
                ? (i18n.__('vaiu.sending') || 'Sending...') 
                : (i18n.__('vaiu.forgot_password.send_reset_link') || 'Send reset link')}
            </Button>

            <div className="text-center text-sm text-gray-600">
              {i18n.__('vaiu.forgot_password.remember_password') || 'Remember your password?'}{' '}
              <Link
                to="/"
                className="text-blue-600 hover:underline"
              >
                {i18n.__('vaiu.sign_in') || 'Sign in'}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
